import React, { ChangeEvent, useRef, useState } from 'react';
import { useStyles } from './MessageActions.styles';
import TooltipOne from '../TooltipOne/TooltipOne.component';
import AttachmentIcon from '@mui/icons-material/Attachment';
import LinkIcon from '@mui/icons-material/Link';
import AnnouncementOutlinedIcon from '@mui/icons-material/AnnouncementOutlined';
import {
  ClickAwayListener,
  Collapse,
  FormHelperTextProps,
  Grid,
  IconButton,
  List,
  ListItemIcon,
  ListItemText,
  TextField,
} from '@material-ui/core';
import { ListItemButton } from '@mui/material';
import { ExpandLess, ExpandMore } from '@material-ui/icons';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import useSessionUser from '../../hooks/useSessionUser';
import { ExaminerFunction } from '../../pages/Assessment/Assessment.types';
import { Prompt } from '../Prompt/Prompt.component';
import { extractFileType, FileType, getMaxFileSizeInBytes } from '../../utils/fileProcessing';
import { Priority } from '../../models/communication.types';
import { ModalWithButtons } from '../ModalWithButtons/ModalWithButtons.component';
import { saveCardLimits } from '../../services/exams/sessions/sessions.service';
import { ValidationRow } from '../ValidationRow/ValidationRow.components';
import { INotificationTypeId, NotificationType } from './NotificationType.component';
import PriorityMediumIcon from '../Mailbox/PriorityMediumIcon.component';
import PriorityHighIcon from '../Mailbox/PriorityHighIcon.component';
import PriorityLowIcon from '../Mailbox/PriorityLowIcon.component';
import { fill } from '../../utils/contrastSvgAttributes';

export interface PriorityListItem {
  text: string;
  value: Priority;
  isVisible: boolean;
  icon: JSX.Element;
  class: string;
}

interface MessageActionsProps {
  priority: Priority;
  onPriorityChange: (priority: Priority) => void;
  onFileChanged?: (file: File) => void;
  onLinkChanged?: (link: string) => void;
  link: string | undefined;
  notificationId: (ids: INotificationTypeId[]) => void;
  canSendHightPriority?: boolean;
  canSendMediumPriority?: boolean;
  isHelpDesk?: boolean;
  isSpecialCaseMessage?: boolean;
  isSmsEnabled?: boolean;
}

export const MessageActions: React.FC<MessageActionsProps> = (props) => {
  const classes = useStyles();
  const { userAssessmentSessionInfo } = useSessionUser();
  const userFunction = userAssessmentSessionInfo.function;

  const [open, setOpen] = useState<boolean>(false);
  const [openType, setOpenType] = useState<boolean>(false);
  const inputFileRef = useRef<HTMLInputElement | null>(null);
  const [formatError, openFormatError] = useState<boolean>(false);
  const [sizeError, openSizeError] = useState<boolean>(false);
  const [formatAndSizeError, openFormatAndSizeError] = useState<boolean>(false);
  const [link, setLink] = useState<string | undefined>(props.link);
  const [linkModalOpen, setLinkModalOpen] = useState<boolean>(false);
  const [linkError, setLinkError] = useState<boolean>(false);
  const [notificationId, setNotificationId] = useState<INotificationTypeId[]>([]);

  const PZE = userFunction === ExaminerFunction.PZE;
  const KGZ = userFunction === ExaminerFunction.KGZ;
  const KK = userFunction === ExaminerFunction.KK;
  const canSendMediumPriority = props.canSendMediumPriority !== undefined ? props.canSendMediumPriority : false;
  const canSendHightPriority = props.canSendHightPriority !== undefined ? props.canSendHightPriority : false;

  const handleClick = () => {
    setOpen(!open);
    setOpenType(false);
  };

  const handleTypeOpen = () => {
    setOpenType(!openType);
    setOpen(false);
  };

  const handlePriority = (priority: Priority) => {
    props.onPriorityChange(priority);
    setOpen(false);
  };

  const priorityListItems: PriorityListItem[] = [
    {
      text: 'Zwykły',
      value: Priority.Low,
      isVisible: true,
      icon: <PriorityLowIcon />,
      class: '',
    },
    {
      text: 'Ważny',
      value: Priority.Medium,
      isVisible: canSendMediumPriority || PZE || KGZ || KK,
      icon: <PriorityMediumIcon />,
      class: classes.importantHover,
    },
    {
      text: 'Wysoki',
      value: Priority.High,
      isVisible: canSendHightPriority || KGZ || KK,
      icon: <PriorityHighIcon />,
      class: classes.highHover,
    },
  ];

  //file upload validation

  const acceptedFileTypes: FileType[] = [
    'application/pdf',
    'image/jpeg',
    'image/png',
    'application/zip',
    'application/x-7z-compressed',
    'text/plain',
    'text/csv',
    'application/vnd.ms-excel' // csv for Firefox
  ];

  const isFileValid = async (file: File): Promise<boolean> => {
    if (
      file.size > getMaxFileSizeInBytes(1) &&
      !acceptedFileTypes.includes(file.type as FileType) &&
      !(await file.arrayBuffer().then((buffer) => acceptedFileTypes.includes(extractFileType(buffer) as FileType)))
    ) {
      openFormatAndSizeError(true);
      return false;
    }
    if (
      !acceptedFileTypes.includes(file.type as FileType) &&
      !(await file.arrayBuffer().then((buffer) => acceptedFileTypes.includes(extractFileType(buffer) as FileType)))
    ) {
      openFormatError(true);
      return false;
    }
    if (file.size > getMaxFileSizeInBytes(20)) {
      openSizeError(true);
      return false;
    }
    return true;
  };

  function isValidHttpUrl(link: string) {
    try {
      const newUrl = new URL(link);
      return newUrl.protocol === 'http:' || newUrl.protocol === 'https:';
    } catch (err) {
      return false;
    }
  }

  const addLink = (e: ChangeEvent<HTMLInputElement>) => {
    setLink(e.target.value);
    setLinkError(false);
  };

  const handleFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length === 1) {
      const file = event.target.files[0];
      isFileValid(file).then((valid) => {
        if (valid && props.onFileChanged) {
          props.onFileChanged(file);
        }
      });
    }
  };

  const handleLink = () => {
    if (link && props.onLinkChanged && isValidHttpUrl(link)) {
      props.onLinkChanged(link);
      setLink(undefined);
      setLinkModalOpen(false);
      setLinkError(false);
    } else {
      setLinkError(true);
    }
  };

  const handleCancelLink = () => {
    setLinkModalOpen(false);
    setLink(undefined);
    setLinkError(false);
  };

  return (
    <>
      <div className={classes.actionsContainer}>
        {props.onFileChanged && (
          <div className={classes.action}>
            <TooltipOne title="Załącznik" placement="top">
              <IconButton disableRipple style={{ padding: '3px' }}>
                <AttachmentIcon
                  className={classes.actionIcon}
                  style={{ transform: 'rotate(320deg)', height: '16px', color: fill }}
                />
                <input
                  type="file"
                  ref={inputFileRef}
                  style={{ opacity: '0', width: '20px', position: 'absolute' }}
                  onChange={(e) => {
                    handleFile(e);
                    e.target.value = '';
                  }}
                  aria-label='załącznik'
                />
              </IconButton>
            </TooltipOne>
          </div>
        )}
        {props.onLinkChanged && (
          <div className={classes.action}>
            <TooltipOne title="Link" placement="top">
              <IconButton
                disableRipple
                disabled={props.link !== undefined}
                style={{ padding: '3px' }}
                onClick={() => setLinkModalOpen(true)}
              >
                <LinkIcon className={classes.actionIcon} style={{ height: '16px', color: fill }} />
              </IconButton>
            </TooltipOne>
          </div>
        )}
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <div className={classes.actionWithBorder}>
            <List component="div" aria-label="Ustaw priorytet lista" style={{ padding: '0' }}>
              <ListItemButton onClick={handleClick} style={{ padding: '0' }}>
                <ListItemIcon style={{ minWidth: '10px' }}>
                  <TooltipOne title="Ustaw priorytet" placement="top">
                    <AnnouncementOutlinedIcon
                      className={classes.actionIcon}
                      style={{ height: '16px', color: fill }}
                    />
                  </TooltipOne>
                </ListItemIcon>
                {open ? (
                  <ExpandLess style={{ width: '0.5em', height: '0.5em' }} />
                ) : (
                  <ExpandMore style={{ width: '0.5em', height: '0.5em' }} />
                )}
              </ListItemButton>
              <Collapse in={open} timeout="auto" unmountOnExit style={{ position: 'absolute', bottom: '25px' }}>
                <List
                  dense
                  style={{
                    border: '1px solid rgba(44, 45, 62, 0.3)',
                    borderRadius: '5px',
                    background: 'white',
                    boxShadow: '0px 0px 3px 3px rgba(171,169,171,0.37)',
                  }}
                >
                  {priorityListItems.map((item: PriorityListItem) => {
                    return (
                      item.isVisible && (
                        <ListItemButton
                          key={item.value}
                          onClick={() => handlePriority(item.value)}
                          className={item.class}
                        >
                          <ListItemIcon style={{ minWidth: '25px' }}>{item.icon}</ListItemIcon>
                          <ListItemText primary={item.text} />
                          <ListItemIcon style={{ justifyContent: 'flex-end' }}>
                            {props.priority !== item.value ? '' : <CheckOutlinedIcon fontSize="small" />}
                          </ListItemIcon>
                        </ListItemButton>
                      )
                    );
                  })}
                </List>
              </Collapse>
            </List>
          </div>
        </ClickAwayListener>
        {!props.isHelpDesk && (
          <ClickAwayListener onClickAway={() => setOpenType(false)}>
            <div className={classes.actionWithBorder}>
              <List component="div" aria-label="Ustaw typ powiadomienia" style={{ padding: '0' }}>
                <ListItemButton onClick={handleTypeOpen} style={{ padding: '0' }}>
                  <ListItemIcon style={{ minWidth: '10px' }}>
                    <TooltipOne title="Ustaw typ powiadomienia" placement="top">
                      <NotificationsOutlinedIcon
                        className={classes.actionIcon}
                        style={{ height: '16px', color: fill }}
                      />
                    </TooltipOne>
                  </ListItemIcon>
                  {openType ? (
                    <ExpandLess style={{ width: '0.5em', height: '0.5em' }} />
                  ) : (
                    <ExpandMore style={{ width: '0.5em', height: '0.5em' }} />
                  )}
                </ListItemButton>
                <NotificationType
                  onNotificationTypeChange={(ids: INotificationTypeId[]) => {
                    setNotificationId(ids);
                    props.notificationId(ids);
                  }}
                  openTypeList={openType}
                  isSmsEnabled={props.isSmsEnabled}
                />
              </List>
            </div>
          </ClickAwayListener>
        )}
      </div>
      <Prompt
        open={sizeError || formatError}
        type={'warning'}
        mainText={sizeError ? `Przesyłany plik jest za duży` : `Wybrany plik ma nieprawidłowy format`}
        subTexts={[
          {
            text: sizeError
              ? `Maksymalna wielkość pliku to 20 MB`
              : `Dopuszczalne formaty to pdf, jpg, jpeg, png, txt, csv, zip, 7zip`,
          },
        ]}
        mainActionButtonText="OK"
        onConfirm={() => {
          openFormatError(false);
          openSizeError(false);
        }}
      />
      <Prompt
        open={formatAndSizeError}
        type={'warning'}
        mainText={`Przesyłany plik jest za duży`}
        subTexts={[
          {
            text: (
              <div>
                Maksymalna wielkość pliku to 20 MB
                <p style={{ fontSize: '20px' }}> i wybrany plik ma nieprawidłowy format </p> Dopuszczalne formaty to
                pdf, jpg, jpeg, png, txt, csv, zip, 7zip
              </div>
            ),
          },
        ]}
        mainActionButtonText="OK"
        onConfirm={() => {
          openFormatAndSizeError(false);
        }}
      />

      <ModalWithButtons
        xCloseButton={handleCancelLink}
        open={linkModalOpen}
        size="xs"
        title={'Dodaj link'}
        autoHeight
        onCancel={handleCancelLink}
        onConfirm={() => handleLink()}
        handleClose={() => setLinkModalOpen(false)}
        confirmButtonDisabled={(link !== undefined && link.length === 0) || link === undefined || linkError}
      >
        <Grid container>
          <Grid item xs={12}>
            <TextField
              value={link}
              onChange={(e: ChangeEvent<HTMLInputElement>) => addLink(e)}
              label="Adres URL"
              variant="outlined"
              fullWidth
              error={linkError}
              aria-label="Adres URL"
              FormHelperTextProps={
                {
                  component: 'div',
                } as Partial<FormHelperTextProps<'p'>>
              }
              helperText={
                <ValidationRow
                  helperText={linkError ? 'Podany adres URL jest nieprawidłowy' : ''}
                  limitsVisible={false}
                  valueLength={0}
                  limit={0}
                  withError={linkError}
                />
              }
              style={{ marginBottom: '10px' }}
            />
          </Grid>
        </Grid>
      </ModalWithButtons>
    </>
  );
};
